import React, { useEffect, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { getFullPath } from '../../utils/pathUtils';
import {
  CHANNEL_CANDIDATES,
  CHANNEL_CONNECT_FAILED,
  ChannelExchange,
  callbackPlatform,
  clearConnectSession,
  readConnectSession,
  stashPendingResult,
} from '../../utils/socialConnect';

const serverHost = process.env.REACT_APP_BACKEND_HOST || 'http://localhost:4000';

const NAMES: Record<string, string> = {
  twitter: 'X',
  x: 'X',
  facebook: 'Facebook',
  threads: 'Threads',
  instagram: 'Instagram',
  youtube: 'YouTube',
  tiktok: 'TikTok',
  linkedin: 'LinkedIn',
  snapchat: 'Snapchat',
};

/**
 * The page a channel's sign-in popup lands on (/auth/<platform>/callback).
 * It trades the code with the API for the channels that sign-in can post to
 * and hands them to the window that opened it, which lets the person pick.
 * Nothing is connected from here.
 */
export function ChannelCallback({ platform }: { platform: string }) {
  const name = NAMES[platform] || platform;
  const [status, setStatus] = useState<'working' | 'done' | 'error'>('working');
  const [message, setMessage] = useState(`Finishing the ${name} sign-in…`);
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return; // React StrictMode runs effects twice in development
    ran.current = true;

    const closeSoon = (ms: number) => {
      setTimeout(() => {
        if (window.opener) window.close();
        else window.location.href = getFullPath('/reach/channels');
      }, ms);
    };

    const run = async () => {
      const params = new URLSearchParams(window.location.search);
      const code = params.get('code');
      const state = params.get('state');
      const denied = params.get('error');
      const session = readConnectSession(platform);
      try {
        if (denied) {
          const why = params.get('error_description') || params.get('error_reason') || denied;
          throw new Error(`${name} did not complete the sign-in (${why}).`);
        }
        if (!code || !state) throw new Error(`${name} did not send a sign-in code.`);
        if (!session) {
          throw new Error(
            'This sign-in was not started from the app. Please connect again from Channels.'
          );
        }
        if (session.state !== state) {
          throw new Error('This sign-in does not match the one you started. Please connect again.');
        }

        const token = localStorage.getItem('token');
        const res = await fetch(
          `${serverHost}/api/auth/${callbackPlatform(platform)}/exchange-token`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
            body: JSON.stringify({
              code,
              codeVerifier: session.codeVerifier,
              accountId: session.accountId,
            }),
          }
        );
        const body = (await res.json().catch(() => ({}))) as Partial<ChannelExchange> & {
          message?: string;
        };
        if (!res.ok || !body.ticket || !body.channels) {
          throw new Error(body.message || `The ${name} sign-in could not be completed.`);
        }
        const result = body as ChannelExchange;

        // The page that started this takes it from here. Leave a copy for the
        // case where that window is gone (then we send the person to Channels).
        stashPendingResult(result);
        if (window.opener) {
          window.opener.postMessage({ type: CHANNEL_CANDIDATES, result }, window.location.origin);
        }
        setStatus('done');
        setMessage(
          result.channels.length === 1
            ? `Signed in to ${name}. You can close this window.`
            : `Signed in to ${name}: ${result.channels.length} channels found. Pick the ones to connect in the app.`
        );
        closeSoon(1200);
      } catch (err) {
        const text =
          err instanceof Error ? err.message : `The ${name} sign-in could not be completed.`;
        setStatus('error');
        setMessage(text);
        if (window.opener) {
          window.opener.postMessage(
            { type: CHANNEL_CONNECT_FAILED, platform, message: text },
            window.location.origin
          );
        }
        closeSoon(6000);
      } finally {
        clearConnectSession(platform);
      }
    };
    void run();
  }, [platform, name]);

  return (
    <div className="flex flex-col items-center justify-center min-h-screen p-6 text-center max-w-xl mx-auto">
      {status === 'working' && <Loader2 className="w-9 h-9 mb-4 text-primary animate-spin" />}
      <h6 className="text-xl font-semibold text-white mb-2">
        {status === 'working' && `Connecting ${name}`}
        {status === 'done' && `${name} signed in`}
        {status === 'error' && `${name} could not be connected`}
      </h6>
      <p className="mt-2 text-cmp-ink-2">{message}</p>
      {status !== 'working' && (
        <p className="mt-4 text-sm text-cmp-ink-3">This window closes on its own.</p>
      )}
    </div>
  );
}

export default ChannelCallback;
